import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';

import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { CAMPAIGN_TYPES, CAMPAIGN_TYPES_ENUM_VALUE } from '@bingads-webui-campaign/competition-auction-insight-preferences';
import {
  fetchPreferenceForGeneralInsights,
  instrumentationSchema,
} from '@bingads-webui-campaign-react/competition-core';
import { ExpLoggerFactory } from '@bingads-webui/experiment-logger';

import { GeneralInsights } from './general-insights';
import { CompetitionRecommendations } from './competition-recommendations';

const CompetitionTabType = {
  AuctionInsights: 'auction-insights',
  RecommendationSummary: 'recommendation-summary',
  RecommendationDetails: 'recommendation-details',
};
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/competition-tab');
const appConfig = globalContext.require('component/config/index');
const preferencesService = globalContext.requireDefault('component/preference/service/index');
const { getSegPreference, getSegType } = globalContext.require('component/bingads-grid/service/segmentation-preferences');
const appState = globalContext.require('component/state/index');
const { fetchSortInfo } = globalContext.require('component/bingads-grid/service/fetch-sortinfo');
const permissions = appConfig.get('Permissions');
const { legacyRouter } = globalContext.require('component/legacy-router/index');

const auctionInsightsBaseRoute = 'competition';
const recommendationsBaseRoute = 'competition_recommendation';

export class CompetitionTab extends React.Component {
  static propTypes = {
    scope: PropTypes.shape({
      customerId: PropTypes.string.isRequired,
      accountId: PropTypes.string.isRequired,
      campaignId: PropTypes.string,
      adGroupId: PropTypes.string,
      campaignName: PropTypes.string,
      campaignType: PropTypes.number,
      token: PropTypes.string.isRequired,
      levelAt: PropTypes.string,
    }).isRequired,
    viewType: PropTypes.string.isRequired,
    el: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]).isRequired,
    grid: PropTypes.shape({
      addDateChangeListener: PropTypes.func.isRequired,
      showAlertHandler: PropTypes.func.isRequired,
    }).isRequired,
    recommendationType: PropTypes.string,
    customDataService: PropTypes.objectOf(PropTypes.any),
    guid: PropTypes.string, // for adinsight view event log in aria
    // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
    perfMarker: PropTypes.object,
  };

  static defaultProps = {
    recommendationType: null,
    customDataService: null,
    guid: null,
  };

  constructor(props) {
    super(props);

    this.initializeInstrumentation();
    this.initializeScope();

    const {
      IsABRecTabTitleGroupB,
      IsABRecTabTitleEnabled,
    } = permissions;

    this.flight = IsABRecTabTitleGroupB ? 'Treatment' : 'Control';
    this.recTabTitle = IsABRecTabTitleEnabled ?
      i18n.getString('Deeper_Insights_Actions') :
      i18n.getString('Deeper_Insights');
    this.expLogger = ExpLoggerFactory.createLogger('AB_RecTabTitle');
    this.logExpInfo = action => this.expLogger.log(action, this.flight);
  }

  state = {
    campaignType: this.getCampaignTypeSettingForAuctionInsights(),
  };

  componentDidMount() {
    const isAuctionInsights = this.props.viewType === CompetitionTabType.AuctionInsights;
    const action = `${isAuctionInsights ? auctionInsightsBaseRoute : recommendationsBaseRoute} Tab clicked. ${!isAuctionInsights ? this.recTabTitle : ''}`;
    this.logExpInfo(action); // only log once for each tab instance

    this.fetchPreferenceToken =
      createAsyncToken(fetchPreferenceForGeneralInsights({
        campaignType: this.state.campaignType,
        preferencesService,
        getSegPreference,
        fetchSortInfo,
        appState,
        getSegType,
      }))
        .on({
          onSuccess: ({ campaignType, gridConfig }) => {
            this.setState({
              campaignType,
              preferenceForGeneralInsights: gridConfig,
            });
          },
        });
  }

  componentDidUpdate() {
    if (this.state.preferenceForGeneralInsights && this.renderWithPreference) {
      this.renderWithPreference.stop();
      this.renderWithPreference = null;
    }
  }

  componentWillUnmount() {
    if (this.fetchPreferenceToken) {
      this.fetchPreferenceToken.cancel();
    }
  }

  onCampaignTypeSelectChange = ({ campaignType }) => {
    this.activity.trace(
      `select campaign type '${campaignType}'`,
      'Competition/onCampaignTypeSelectChange'
    );

    if (this.fetchPreferenceToken) {
      this.fetchPreferenceToken.cancel();
    }

    this.fetchPreferenceToken = createAsyncToken(fetchPreferenceForGeneralInsights({
      campaignType,
      preferencesService,
      getSegPreference,
      fetchSortInfo,
      appState,
      getSegType,
    }))
      .on({
        onSuccess: ({ gridConfig }) => {
          this.setState({
            campaignType,
            preferenceForGeneralInsights: gridConfig,
          });
        },
      });
  }

  /**
   * Get campaign type that will be used in fetching auction insights
   * @param {bool} fetch only dynamically fetch campaign type when fetch is true.
   * @returns {string} campaign type that will be used in fetching auction insights.
   */
  getCampaignTypeSettingForAuctionInsights() {
    if (this.props.viewType !== CompetitionTabType.AuctionInsights) {
      return CAMPAIGN_TYPES.Search;
    }
    if (this.props.scope.campaignType) {
      // pagecontext only has enum value.
      const shoppingEnum = CAMPAIGN_TYPES_ENUM_VALUE[CAMPAIGN_TYPES.Shopping];
      return this.props.scope.campaignType === shoppingEnum ?
        CAMPAIGN_TYPES.Shopping : CAMPAIGN_TYPES.Search;
    }

    return null; // when null, will use preference to decide final value.
  }

  initializeInstrumentation() {
    instrumentation.addScenario(instrumentationSchema);
    this.currentScenario = instrumentation.loadCompetitionTab.create();
    this.renderWithPreference = this.currentScenario.renderWithPreference.create();
    this.renderWithPreference.start();
  }

  initializeScope = () => {
    const {
      accountId,
      campaignId,
      adGroupId,
    } = this.props.scope;
    let levelAt = levelAtConstant.ACCOUNT;
    let entityIds = [accountId];

    if (adGroupId) {
      levelAt = levelAtConstant.ADGROUP;
      entityIds = [adGroupId];
    } else if (campaignId) {
      levelAt = levelAtConstant.CAMPAIGN;
      entityIds = [campaignId];
    }
    this.renderWithPreference.trace(`scope is ${levelAt}`, 'Competition/initializeScope');

    this.scope = _.defaults({}, this.props.scope, {
      levelAt,
      entityIds,
    });
  };

  navigate = baseRoute => () => {
    let route = baseRoute;
    switch (this.scope.levelAt) {
      case levelAtConstant.CAMPAIGN:
        route = `campaign_${baseRoute}`;
        break;
      case levelAtConstant.ADGROUP:
        route = `adgroup_${baseRoute}`;
        break;
      case levelAtConstant.ACCOUNT:
      default:
        break;
    }

    legacyRouter.navigateToRoute(route);
  }

  route = ({
    accountLevelRouteName,
    additionalParams,
    scope,
  }) => {
    const {
      scope: {
        customerId,
        accountId,
        campaignId,
        adGroupId,
      },
    } = this.props;

    let route = '';
    let params = {
      customer_id: customerId,
      account_id: accountId,
      campaign_id: campaignId,
      adgroup_id: adGroupId,
    };
    _.extend(params, additionalParams);

    const levelAt = scope || this.props.scope.levelAt;
    switch (levelAt) {
      case levelAtConstant.CAMPAIGN:
        route = `campaign_${accountLevelRouteName}`;
        params = _.omit(params, 'adgroup_id');
        break;
      case levelAtConstant.ADGROUP:
        route = `adgroup_${accountLevelRouteName}`;
        break;
      case levelAtConstant.ACCOUNT:
      default:
        route = accountLevelRouteName;
        params = _.omit(params, 'campaign_id', 'adgroup_id');
        break;
    }

    legacyRouter.navigateToRoute(route, params);
  }


  viewSummary = (op) => {
    this.route(_.defaults({
      accountLevelRouteName: 'competition_recommendation',
    }, op));
  };

  viewDetails = type => () => {
    this.route({
      accountLevelRouteName: 'competition_recommendation_details',
      additionalParams: { type },
    });
  };

  render() {
    const isAuctionInsights = this.props.viewType === CompetitionTabType.AuctionInsights;
    const mainView = isAuctionInsights ?
      (
        <div railviewid="Competition_AuctionInsights">
          {this.state.preferenceForGeneralInsights &&
            <GeneralInsights
              key={this.state.campaignType}
              {..._.pick(this.props, 'el', 'grid', 'customDataService', 'perfMarker')}
              scope={this.scope}
              campaignType={this.state.campaignType}
              onCampaignTypeSelectChange={this.onCampaignTypeSelectChange}
              preferenceConfig={this.state.preferenceForGeneralInsights}
              parentScenario={this.currentScenario}
            />
          }
        </div>
      ) : (
        <div railviewid="Competition_Recommendations">
          <CompetitionRecommendations
            {..._.pick(this.props, 'grid', 'recommendationType', 'el', 'guid', 'perfMarker')}
            scope={this.scope}
            parentScenario={this.currentScenario}
            logExpInfo={this.logExpInfo}
            viewSummary={this.viewSummary}
            viewDetails={this.viewDetails}
          />
        </div>
      );

    this.activity = isAuctionInsights ?
      this.currentScenario.viewGeneralInsights.create() :
      this.currentScenario.viewDeeperInsights.create();

    if (isAuctionInsights && this.state.preferenceForGeneralInsights) {
      this.activity.trace(
        `render the competition auction insights view with selected campaign type '${this.state.campaignType}'`,
        'Competition/render'
      );
    } else if (isAuctionInsights) {
      this.activity.trace('preference is not back yet for competition auction insights', 'Competition/render');
    } else {
      this.activity.trace('render the competition recommendation view', 'Competition/render');
    }

    return (
      <div className="ba-competition-tab">
        <ul className="nav navbar-nav" >
          <li className={isAuctionInsights ? 'active' : ''}>
            {/* eslint-disable jsx-a11y/anchor-is-valid, no-script-url */}
            <a className={CompetitionTabType.AuctionInsights} href="#!" onClick={this.navigate(auctionInsightsBaseRoute)}>
              {i18n.getString('Broad_Insights')}
            </a>
          </li>
          {permissions.IsBAACompetitionRecommendationsEnabled &&
            <li className={isAuctionInsights ? '' : 'active'}>
              <a className={CompetitionTabType.RecommendationSummary} href="#!" onClick={this.navigate(recommendationsBaseRoute)}>
                {this.recTabTitle}
              </a>
            </li>
          }
        </ul>
        {mainView}
      </div>
    );
  }
}

